import React from "react"

import { styled } from "@mui/styles"

import { getFileType } from "@cirrus/common/helpers/getFileType"
import { useTranslations } from "@cirrus/core/i18n/provider"
import { ActionIcon, FileIcon, MuiIcon, ProgressBar, Tooltip } from "@cirrus/ui-kit-ts"

interface IProps {
  title: string,
  index: number,
  size: number,
  fileCount: number,
  maxAttachments: number,
  maxAttachmentSize: number,
  supportedFileTypes: string[],
  onRemove: (index: number) => void,
}

export const Attachment = ({
  title,
  index,
  size,
  fileCount,
  maxAttachments,
  maxAttachmentSize,
  supportedFileTypes,
  onRemove
}: IProps) => {
  const { i18n } = useTranslations()

  if (!title) {
    return <></>
  }

  const type = getFileType(title)

  const outOfSize = size > maxAttachmentSize
  const isSupportedFile = supportedFileTypes.includes(type) && !outOfSize
  const outOfLimit = fileCount > maxAttachments

  return <StyledAttachment key={index}>
    <FileIcon fileType={type} />

    <Wrapper>
      <TitleWrapper>
        <TitleContainer>
          {
            (!isSupportedFile || outOfLimit)
            && <MuiIcon.InfoOutlined
              color="error"
              sx={{ fontSize: "1.6rem" }} />
          }

          <Title><Tooltip title={title}>{title}</Tooltip></Title>
        </TitleContainer>

        {
          isSupportedFile && outOfLimit
          && <Error>{i18n("General:MaximumAttachments")}: {maxAttachments}</Error>
        }
        {
          !isSupportedFile && !outOfSize
          && <Error>{i18n("General:WrongFileFormat")}</Error>
        }
        {
          outOfSize
          && <Error>
            {`${i18n("Admin.Vpl:MaxFileSize")} ${maxAttachmentSize / 1000 / 1000}MB`}
          </Error>
        }
      </TitleWrapper>

      <ProgressBar
        value={isSupportedFile ? 100 : 20}
        color={isSupportedFile ? "success" : "error"} />
    </Wrapper>

    {
      isSupportedFile
      && <MuiIcon.Check
        className="checkIcon"
        color="success"
        sx={{ fontSize: "1.8rem" }} />
    }

    <ActionIcon
      className={isSupportedFile ? "deleteIcon" : ""}
      icon={MuiIcon.Close}
      onClick={() => onRemove(index)}
      color="error" />
  </StyledAttachment>

}

const StyledAttachment = styled("div")({
  padding: ".8rem 1.2rem",
  background: "#F2F2F2",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  "&:not(:hover) > .deleteIcon": {
    display: "none"
  },

  "&:hover > .checkIcon": {
    display: "none"
  }
})

const Error = styled("div")({
  color: "#DF4C47"
})

const TitleWrapper = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "1rem",
})

const TitleContainer = styled("div")({
  display: "flex",
  gap: ".4rem",
  alignItems: "center",
})

const Title = styled("div")({
  maxWidth: "18rem",
  lineHeight: "1.6rem",
  whiteSpace: "nowrap",
  overflowX: "clip",
  textOverflow: "ellipsis",
})

const Wrapper = styled("div")({
  width: "28rem",
  maxWidth: "28rem",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  lineHeight: "1.4rem",
  gap: ".4rem",
  fontSize: "1.4rem"
})
